import React, { createContext, useCallback, useContext } from 'react';

import { IProviderProps } from '../@types/ContextProviderRelated';
import { EWallMode } from '../@types/Enums';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import useUndoRedo from '../hooks/useUndoRedo';
import { useBuilding } from './BuildingProvider/BuildingProvider';
import { useSelection } from './SelectionProvider';
import { useToolbarContext } from './ToolbarProvider';

interface IKeyboardShortcutContext {}

export const KeyboardShortcutContext = createContext<IKeyboardShortcutContext | undefined>(undefined);

export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutContext);
  if (!context) {
    throw new Error('useKeyboardShortcuts must be used within a KeyboardShortcutProvider');
  }
  return context;
};

export function KeyboardShortcutProvider({ children }: IProviderProps): JSX.Element {
  const { setWallMode } = useToolbarContext();
  const { resetSelectionWithoutHistoryDrawer } = useSelection();
  const { setBuilding } = useBuilding();
  const { undo, redo } = useUndoRedo();

  const handleShortcutUndo = useCallback(() => {
    resetSelectionWithoutHistoryDrawer();
    setWallMode(EWallMode.NONE);
    undo(setBuilding);
  }, [resetSelectionWithoutHistoryDrawer, setBuilding, setWallMode, undo]);

  const handleShortcutRedo = useCallback(() => {
    resetSelectionWithoutHistoryDrawer();
    setWallMode(EWallMode.NONE);
    redo(setBuilding);
  }, [resetSelectionWithoutHistoryDrawer, setBuilding, setWallMode, redo]);

  // Register shortcut UNDO (Cmd/Ctrl + Z)
  useKeyboardShortcut(
    ['z'],
    () => {
      handleShortcutUndo();
    },
    { ctrlKey: true, metaKey: true, shiftKey: false },
  );

  // Register shortcut REDO (Mac Standard: Cmd + Shift + Z, Windows Alternative: Ctrl + Shift + Z)
  useKeyboardShortcut(
    ['z'],
    () => {
      handleShortcutRedo();
    },
    { ctrlKey: true, metaKey: true, shiftKey: true },
  );

  // Register shortcut REDO (Windows/Linux Standard: Ctrl + Y)
  // This is the common, simplified Redo command for Windows users.
  useKeyboardShortcut(
    ['y'],
    () => {
      handleShortcutRedo();
    },
    // We explicitly set metaKey: false to prevent Cmd + Y from triggering,
    // as Mac users expect Cmd + Shift + Z for Redo.
    { ctrlKey: true, metaKey: false, shiftKey: false },
  );

  return <KeyboardShortcutContext.Provider value={{}}>{children}</KeyboardShortcutContext.Provider>;
}
